// @ts-strict-ignore
import type { PropsWithChildren } from 'react';
import React, { Fragment, useState } from 'react';
import type { FieldValues, UseFormSetError } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { ButtonVariant } from '@usealloy/component-library';
import {
  Box,
  TextField,
  Tooltip,
  Button,
  Text,
} from '@usealloy/component-library';

import { StyledForm } from './styles';
import type { InputFields } from './types';
import { RED } from '~/constants';

interface Props {
  fields: InputFields;
  onSubmit: (
    formData: FieldValues,
    onSubmitOptionms?: { setError: UseFormSetError<FieldValues> }
  ) => void;
  onSubmitActionText?: string | false;
  validChecks?: boolean;
  warning?: string;
  customSubmitButtonStyle?: Record<string, any>;
  submitButtonVariant?: ButtonVariant;
  centerSubmit?: boolean;
}

const getSubmitButtonPosition = (hasChildren, centerSubmit) => {
  if (centerSubmit) {
    return 'center';
  }

  return hasChildren ? 'space-between' : 'flex-end';
};

const FormContainer = ({
  fields,
  onSubmit,
  onSubmitActionText = 'Sign In',
  customSubmitButtonStyle,
  children,
  validChecks = true,
  warning = '',
  submitButtonVariant,
  centerSubmit,
}: PropsWithChildren<Props>) => {
  const {
    register, handleSubmit, setValue, formState, setError,
  } = useFormContext();

  const { isSubmitting, isValidating, errors } = formState;

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const handleTelInput = (e, fieldName: string) => {
    let str = e.target.value;
    const onlyNums = /^[\d]*$/;

    if (str.length === 10 && onlyNums.test(str)) {
      const firstPart = str.slice(0, 3);
      const secondPart = str.slice(3, 6);
      const thirdPart = str.slice(6);
      str = `${firstPart}-${secondPart}-${thirdPart}`;
    }

    if (str.length === 4 && onlyNums.test(str)) {
      str = `${str.slice(0, 3)}-${str.slice(-1)}`;
    }

    if (str.length === 8 && onlyNums.test(str.slice(-4))) {
      str = `${str.slice(0, 7)}-${str.slice(-1)}`;
    }

    if (str.length > 12) {
      return;
    }

    setValue(fieldName, str);
  };

  return (
    <StyledForm
      data-testid="authentication-form"
      onSubmit={handleSubmit(formData => onSubmit(formData, { setError }))}
    >
      <Box direction="column" gap={6}>
        {fields.map(field => (field.hasTooltip ? (
          <Tooltip
            key={field.label}
            content={
                field.toolTipContent.type === 'method'
                  ? field.toolTipContent.method()
                  : field.toolTipContent.text
              }
              // @ts-ignore: 2322 - @todo: check tooltip component types
            open={tooltipOpen}
            side="right"
            variant="secondary"
            style={{ maxWidth: '273px' }}
          >
            <TextField
              type={field.type}
              error={!!errors[field.name]}
              key={field.label}
              label={field.label}
              name={field.name}
                // @ts-ignore: 2322 - @todo: textfield component lacks the type for this
              {...register(field.name, {
                ...field,
                onChange:
                    field.type === 'tel'
                      ? (e: React.FormEvent<HTMLInputElement>) => {
                        setTooltipOpen(true);
                        handleTelInput(e, field.name);
                      }
                      : () => setTooltipOpen(true),
              })}
              onFocus={() => {
                setTooltipOpen(true);
              }}
              onBlur={() => {
                setTooltipOpen(false);
              }}
              placeholder={field.placeholder}
            />
          </Tooltip>
        ) : (
          <Fragment key={field.label}>
            <TextField
              type={field.type}
              error={!!errors[field.name]}
              key={field.label}
              label={field.label}
              name={field.name}
              placeholder={field.placeholder}
                // @ts-ignore: 2322 - @todo: textfield component lacks the type for this
              {...register(field.name, {
                ...field,
                ...(field.type === 'tel' && {
                  onChange: e => handleTelInput(e, field.name),
                }),
              })}
            />
            {errors.phoneNumber?.message && (
            <Text
              style={{
                color: RED.red500,
                alignSelf: 'center',
                marginTop: '-16px',
              }}
            >
              {errors.phoneNumber.message as unknown as React.ReactNode}
            </Text>
            )}
          </Fragment>
        )),
        )}
        {warning && (
          <Box
            style={{ color: 'black', fontSize: '12px', textAlign: 'center' }}
          >
            {warning}
          </Box>
        )}
        <Box
          direction="row"
          alignItems="center"
          justifyContent={getSubmitButtonPosition(!!children, centerSubmit)}
        >
          {children}
          {onSubmitActionText && (
            <Button
              {...(submitButtonVariant ? { variant: submitButtonVariant } : {})}
              style={customSubmitButtonStyle ?? {}}
              disabled={isSubmitting || isValidating || !validChecks}
              type="submit"
              data-testid="signInButton"
              data-value={onSubmitActionText}
            >
              {onSubmitActionText}
            </Button>
          )}
        </Box>
      </Box>
    </StyledForm>
  );
};

export default FormContainer;
